import type { PiniaPluginContext, Store } from 'pinia';

type StoreWithReset = Store & { reset?: () => void };

/**
 * 深拷貝 store 的初始 state。
 *
 * 不用 structuredClone：$state 是 reactive proxy，結構化拷貝會直接拋 DataCloneError。
 * 也不用 JSON：那會把值為 undefined 的欄位整個吃掉，還原時就漏還原。
 * 這裡逐層讀屬性（讀 proxy 會自動解包 ref），保留 undefined、Date、Map、Set。
 */
const deepClone = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((item) => deepClone(item)) as T;
  }
  if (value instanceof Date) {
    return new Date(value.getTime()) as T;
  }
  if (value instanceof Map) {
    return new Map([...value].map(([key, item]) => [key, deepClone(item)])) as T;
  }
  if (value instanceof Set) {
    return new Set([...value].map((item) => deepClone(item))) as T;
  }
  if (value !== null && typeof value === 'object') {
    const cloned: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      cloned[key] = deepClone(item);
    }
    return cloned as T;
  }
  return value;
};

/**
 * 幫每個 store 補上 reset()。
 *
 * setup store（我們全部都是這種寫法）沒有內建的 $reset，過去靠每個 store 自己手寫一份 reset()，
 * useResetStore 再 duck-typing 呼叫——漏寫的 store 只會印一行 warn 就被跳過，
 * 結果是登出／換帳號後那些 store 還帶著上一個人的資料，而且新增 store 時很容易再漏一次。
 *
 * 這裡在 store 建立當下記住初始 state，替沒有手寫 reset() 的 store 補一份還原用的。
 * 已經自己實作 reset() 的（例如需要一併清 cookie 的 auth store）維持原樣，不覆寫。
 *
 * 注意快照的是「store 第一次被建立時」的 state：若某個 store 是由 SSR payload hydrate 出來的，
 * 還原點就是那份 payload 而不是宣告時的預設值。需要更嚴格語意的 store 請自己實作 reset()。
 */
export function storeAutoResetPlugin({ store }: PiniaPluginContext) {
  const target = store as StoreWithReset;
  if (typeof target.reset === 'function') {
    return;
  }

  const initialState = deepClone(store.$state);

  target.reset = () => {
    // 每次都給一份新的拷貝，否則會把巢狀物件的參考接進 store，下一次就還原不回來了
    const fresh = deepClone(initialState) as Record<string, unknown>;

    // 不能用 `$patch(物件)`：那條路是 deep merge —— 初始快照沒有、但這個 session 期間
    // 才長出來的 key 會原封不動留著（Record 以 id 當 key 的快取、Map / Set 的項目都是）。
    // 這個 plugin 存在的意義就是登出／換帳號時清掉上一個人的資料，留著等於沒清。
    // 改用函式寫法自己接管：先刪掉快照沒有的 root key，再逐個 root key **整份取代**。
    store.$patch(($state) => {
      const state = $state as Record<string, unknown>;
      for (const key of Object.keys(state)) {
        if (!(key in fresh)) delete state[key];
      }
      // Object.assign 是逐個 root key 覆寫，不會遞迴合併，
      // 所以巢狀的 Record / Map / Set 是被整份換掉而不是併上去
      Object.assign(state, fresh);
    });
  };
}
